import React, { useState } from 'react';

import { Alert, Box, Button, CircularProgress, Stack, Typography } from '@mui/material';

import { useGetFriendsAndFamilyUsage } from '~/hooks/apis/promo/useGetFriendsAndFamilyUsage';

import type { GetFriendsAndFamilyUsageQuery } from '~/services/PromoService';
import { formatDateLongMonthWithMeridiem } from '~/services/TimeService';

import { isPromoBrand } from '../promoBrandUtils';

function FriendsAndFamilyUsageWidget({ tenant }: { tenant: App.Tenant }) {
  const [query, setQuery] = useState<GetFriendsAndFamilyUsageQuery | null>(null);
  const usageQuery = useGetFriendsAndFamilyUsage(query);
  // svc-promo does not expose white-label brands; yidu uses the same promo configuration as LE.
  const promoBrand = tenant.brand === 'yidu' ? 'luxuryescapes' : isPromoBrand(tenant.brand) ? tenant.brand : undefined;

  const handleLookup = () => {
    if (!promoBrand) {
      return;
    }

    setQuery({ brand: promoBrand });
  };

  return (
    <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 2 }}>
      <Stack spacing={2}>
        <Box>
          <Typography component="h3" variant="subtitle1">
            Friends &amp; Family usage
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Check your current Friends &amp; Family allowance for {tenant.title}.
          </Typography>
        </Box>
        {!promoBrand && <Alert severity="info">Friends &amp; Family usage is not available for this brand.</Alert>}
        <Stack direction="row" justifyContent="flex-start">
          <Button disabled={!promoBrand || usageQuery.isFetching} onClick={handleLookup} variant="contained">
            {usageQuery.isFetching ? 'Checking…' : 'Check usage'}
          </Button>
        </Stack>
        {usageQuery.isFetching && <CircularProgress size={24} />}
        {usageQuery.isError && <Alert severity="error">{usageQuery.error.message || 'Usage lookup failed.'}</Alert>}
        {usageQuery.isSuccess &&
          (usageQuery.data.result.length > 0 ? (
            <Stack spacing={1}>
              {usageQuery.data.result.map((usage) => (
                <Box key={usage.codeName} sx={{ bgcolor: 'action.hover', borderRadius: 1, p: 1.5 }}>
                  <Typography variant="subtitle2">{usage.codeName}</Typography>
                  <Typography variant="body2">
                    {usage.usagesRemaining} of {usage.usageLimit} uses remaining ({usage.usageCount} used)
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Current period: {formatDateLongMonthWithMeridiem(usage.periodStartedAt)} to{' '}
                    {formatDateLongMonthWithMeridiem(usage.resetsAt)}
                  </Typography>
                </Box>
              ))}
            </Stack>
          ) : (
            <Alert severity="info">No Friends &amp; Family usage applies to this member.</Alert>
          ))}
      </Stack>
    </Box>
  );
}

export default FriendsAndFamilyUsageWidget;
